import React, { useState } from 'react'
import { Input } from '../ui/input'
import { ScrollArea } from '../ui/scroll-area'
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Select as UISelect,
} from '../ui/select'
import { FormLabel } from './form-label'

export type Option = {
  value: string
  label: string
  description?: string
}

export function Select({
  field,
  label,
  options,
  placeholder = 'Select...',
  size = 'default',
  ...props
}: {
  field: any
  label?: string
  options: Array<Option>
  placeholder?: string
  size?: 'sm' | 'default'
  id?: string
  name?: string
  required?: boolean
  searchable?: boolean
}) {
  const [search, setSearch] = React.useState('')
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  )
  return (
    <div className="gap-0 w-full">
      {label && (
        <FormLabel htmlFor={props.name} required={props.required}>
          {label}
        </FormLabel>
      )}
      <UISelect
        value={field.state.value}
        onValueChange={field.handleChange}
        {...props}
      >
        <SelectTrigger
          size={size}
          id={props.id}
          name={props.name}
          className="w-full"
        >
          <SelectValue>
            {field.state.value
              ? options.find((opt) => opt.value === field.state.value)?.label ||
                field.state.value
              : placeholder}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {props.searchable && (
            <div className="sticky top-0 z-10 bg-white p-2 border-b">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full mb-2 px-2 py-1 border rounded text-sm"
              />
            </div>
          )}
          <ScrollArea className="max-h-50">
            {filteredOptions.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                title={opt.description || undefined}
                className="hover:cursor-pointer"
              >
                {opt.label}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </UISelect>
      {field.state.meta.isTouched && !field.state.meta.isValid ? (
        <p className="text-xs text-red-500">
          {field.state.meta.errors.join(', ')}
        </p>
      ) : null}
      <p className="text-xs">
        {field.state.meta.isValidating ? 'Validating...' : null}
      </p>
    </div>
  )
}

export function SelectMulti({
  field,
  label,
  options,
  placeholder = 'Select...',
  size = 'default',
  ...props
}: {
  field: any
  label?: string
  options: Array<Option>
  placeholder?: string
  size?: 'sm' | 'default'
  id?: string
  name?: string
  required?: boolean
  searchable?: boolean
}) {
  const [search, setSearch] = useState('')

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  )

  const handleSelect = (optionValue: string) => {
    if (field.state.value.includes(optionValue)) {
      field.handleChange(
        field.state.value.filter((v: string) => v !== optionValue),
      )
    } else {
      field.handleChange([...field.state.value, optionValue])
    }
  }

  return (
    <div className="gap-0 w-full">
      {label && (
        <FormLabel htmlFor={props.name} required={props.required}>
          {label}
        </FormLabel>
      )}
      <UISelect {...props} onValueChange={handleSelect}>
        <SelectTrigger
          size={size}
          id={props.id}
          name={props.name}
          className="w-full"
        >
          <SelectValue>
            {field.state.value.length === 0
              ? placeholder
              : options
                  .filter((opt) => field.state.value.includes(opt.value))
                  .map((opt) => opt.label)
                  .join(', ')}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {props.searchable && (
            <div className="sticky top-0 z-10 bg-white p-2 border-b">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full mb-2 px-2 py-1 border rounded text-sm"
              />
            </div>
          )}
          <ScrollArea className="max-h-50">
            {filteredOptions.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                aria-selected={field.state.value.includes(opt.value)}
                title={opt.description || undefined}
                className="hover:cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={field.state.value.includes(opt.value)}
                  readOnly
                  className="hidden"
                />
                {opt.label}
              </SelectItem>
            ))}
          </ScrollArea>
        </SelectContent>
      </UISelect>
      {field.state.meta.isTouched && !field.state.meta.isValid ? (
        <p className="text-xs text-red-500">
          {field.state.meta.errors.join(', ')}
        </p>
      ) : null}
      <p className="text-xs">
        {field.state.meta.isValidating ? 'Validating...' : null}
      </p>
    </div>
  )
}
